import React from 'react';
import { ArrowRight, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const Hero = () => {
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const openCalendly = () => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: 'https://calendly.com/gr-prosperity-nicklas-reichert/45-minute-expat-consultation-gr-prosperity'
      });
    }
    return false;
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden bg-gradient-to-br from-gray-50 via-white to-orange-50/30">
      {/* Background decorative elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Logo Badge */}
            <div className="inline-flex items-center space-x-5 mb-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_settle-de/artifacts/ei2t4zd1_20251110_GRP%20black_Private%20investments.jpg" 
                alt="GR Prosperity Logo" 
                className="h-28 w-28 object-contain"
                style={{ mixBlendMode: 'multiply' }}
              />
              <div>
                <div className="text-base font-semibold text-gray-600 uppercase tracking-wide">GR. Prosperity</div>
                <div className="text-xl font-bold text-orange-500">For Expats in Germany</div>
              </div>
            </div>

            <div className="inline-block">
              <span className="inline-flex items-center px-4 py-2 rounded-full bg-orange-100 text-orange-700 text-sm font-medium">
                <span className="mr-2">🌍</span>
                Your Partner for a Successful Start in Germany
              </span>
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              Navigate Your
              <span className="block mt-2 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                German Journey
              </span>
              with Confidence
            </h1>

            <p className="text-xl text-gray-600 leading-relaxed">
              Comprehensive financial planning and relocation support tailored specifically for expats moving to Germany. We handle the complexity so you can focus on your new beginning.
            </p>

            {/* Key Benefits */}
            <div className="space-y-3">
              {[
                'Independent advice with access to 95% of German market products',
                'Expert guidance in English for all financial matters',
                'Streamlined setup for banking, insurance & investments'
              ].map((benefit, index) => (
                <div key={index} className="flex items-start space-x-3">
                  <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button
                onClick={openCalendly}
                size="lg"
                className="bg-orange-500 hover:bg-orange-600 text-white text-lg px-8 py-6 rounded-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105"
              >
                Schedule Free Consultation
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                onClick={() => {
                  const element = document.getElementById('services');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-lg border-2 border-gray-300 hover:border-orange-500 hover:text-orange-500 transition-all duration-300"
              >
                Learn More
              </Button>
            </div>

            {/* Trust Indicators */}
            <div className="pt-8 border-t border-gray-200">
              <div className="grid grid-cols-3 gap-8">
                <div>
                  <div className="text-3xl font-bold text-orange-500">500+</div>
                  <div className="text-sm text-gray-600 mt-1">Expats Supported</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-500">95%</div>
                  <div className="text-sm text-gray-600 mt-1">Market Coverage</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-orange-500">100%</div>
                  <div className="text-sm text-gray-600 mt-1">Independent</div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Image/Illustration */}
          <div className="relative lg:block hidden">
            <div className="relative w-full h-[600px] rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1467269204594-9661b134dd2b?w=800&q=80"
                alt="Modern German cityscape"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-orange-500/20 to-transparent"></div>
              
              {/* Floating Card */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-md p-6 rounded-xl shadow-xl">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="w-7 h-7 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">All-in-One Solution</div>
                    <div className="text-sm text-gray-600">Finance • Insurance • Investments • Real Estate</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
